<script lang="ts">
	import { Stack, Select, Input, Text, Collapse } from '@Shelly/shelly-design-system';
	import { flowEditorState } from '../../../../stores/flow_editor';
	import { user_settings } from '../../../../stores';

	export let nodeId: string;

	// Get node data
	$: node = $flowEditorState.nodes.find((n) => n.id === nodeId);
	$: nodeData = (node?.data || {}) as any;

	// Weather condition types
	const weatherConditions = [
		{ value: 'temperature', title: 'Temperature' },
		{ value: 'humidity', title: 'Humidity' },
		{ value: 'clouds', title: 'Cloud Coverage' },
		{ value: 'wind_speed', title: 'Wind Speed' },
		{ value: 'wind_force', title: 'Wind Force' },
		{ value: 'wind_direction', title: 'Wind Direction' },
		{ value: 'chance_precipitation', title: 'Chance of Precipitation' },
	];

	// Operators
	const numericOperators = [
		{ value: 'equal', title: 'Equal to' },
		{ value: 'greater_than', title: 'Greater than' },
		{ value: 'less_than', title: 'Less than' },
	];

	const equalOnlyOperator = [{ value: 'equal', title: 'Equal to' }];

	// Cloud coverage options
	const cloudOptions = [
		{ value: 'clear_sky', title: 'Clear Sky (0-10%)' },
		{ value: 'partly_cloudy', title: 'Partly Cloudy (11-25%)' },
		{ value: 'mostly_cloudy', title: 'Mostly Cloudy (26-50%)' },
		{ value: 'cloudy', title: 'Cloudy (51-84%)' },
		{ value: 'overcast', title: 'Overcast (85-100%)' },
	];

	// Wind direction options
	const windDirectionOptions = [
		{ value: 'north', title: 'North (N)' },
		{ value: 'north_northeast', title: 'North-Northeast (NNE)' },
		{ value: 'northeast', title: 'Northeast (NE)' },
		{ value: 'east_northeast', title: 'East-Northeast (ENE)' },
		{ value: 'east', title: 'East (E)' },
		{ value: 'east_southeast', title: 'East-Southeast (ESE)' },
		{ value: 'southeast', title: 'Southeast (SE)' },
		{ value: 'south_southeast', title: 'South-Southeast (SSE)' },
		{ value: 'south', title: 'South (S)' },
		{ value: 'south_southwest', title: 'South-Southwest (SSW)' },
		{ value: 'southwest', title: 'Southwest (SW)' },
		{ value: 'west_southwest', title: 'West-Southwest (WSW)' },
		{ value: 'west', title: 'West (W)' },
		{ value: 'west_northwest', title: 'West-Northwest (WNW)' },
		{ value: 'northwest', title: 'Northwest (NW)' },
		{ value: 'north_northwest', title: 'North-Northwest (NNW)' },
	];

	// Wind force options (Beaufort scale)
	const windForceOptions = [
		{ value: 'calm', title: 'Calm (<1 km/h)' },
		{ value: 'light_air', title: 'Light Air (1-5 km/h)' },
		{ value: 'light_breeze', title: 'Light Breeze (6-11 km/h)' },
		{ value: 'gentle_breeze', title: 'Gentle Breeze (12-19 km/h)' },
		{ value: 'moderate_breeze', title: 'Moderate Breeze (20-28 km/h)' },
		{ value: 'fresh_breeze', title: 'Fresh Breeze (29-38 km/h)' },
		{ value: 'strong_breeze', title: 'Strong Breeze (39-49 km/h)' },
		{ value: 'near_gale', title: 'Near Gale (50-61 km/h)' },
		{ value: 'gale', title: 'Gale (62-74 km/h)' },
		{ value: 'strong_gale', title: 'Strong Gale (75-88 km/h)' },
		{ value: 'storm', title: 'Storm (89-102 km/h)' },
		{ value: 'violent_storm', title: 'Violent Storm (103-117 km/h)' },
		{ value: 'hurricane', title: 'Hurricane (>117 km/h)' },
	];

	// Execution types
	const executionTypes = [
		{ value: 'once', title: 'When value becomes true' },
		{ value: 'any', title: 'Every time value is true' },
	];

	// Hour offset options (0-48 hours ahead)
	const hourOffsets = Array.from({ length: 49 }, (_, i) => ({
		value: i,
		title: i === 0 ? 'Current hour' : `+${i} hour${i > 1 ? 's' : ''}`,
	}));

	const normalizeWeatherCondKey = (data: any): string => {
		const raw = data?.weatherCond ?? data?.cond;
		const s = typeof raw === 'string' ? raw.trim() : '';
		if (s && weatherConditions.some((c) => c.value === s)) {
			return s;
		}
		return 'temperature';
	};

	/** Scene `OPERATORS` uses `more_than`; Flow `toSceneJson` / options use `greater_than`. */
	const normalizeWeatherOperatorKey = (data: any): string => {
		const raw = data?.operator ?? data?.op ?? 'equal';
		const s = String(raw).trim().toLowerCase().replace(/\s+/g, '_');
		if (s === 'more_than' || s === 'gt' || s === 'greater_than') {
			return 'greater_than';
		}
		if (s === 'less_than' || s === 'lt') {
			return 'less_than';
		}
		if (s === 'equal' || s === 'eq') {
			return 'equal';
		}
		return 'equal';
	};

	/** Resolves forecast hour from editor (`hourOffset`) or scene-rebuilt (`hours`) data; coerces numbers and legacy title strings. */
	const normalizeForecastHourOffset = (data: Record<string, unknown>): number => {
		const raw = data.hourOffset ?? data.hours;
		if (typeof raw === 'number' && Number.isFinite(raw)) {
			return Math.min(48, Math.max(0, Math.trunc(raw)));
		}
		if (typeof raw === 'string') {
			const trimmed = raw.trim();
			if (trimmed === '') {
				return 0;
			}
			const asInt = parseInt(trimmed, 10);
			if (!Number.isNaN(asInt) && String(asInt) === trimmed) {
				return Math.min(48, Math.max(0, asInt));
			}
			const byTitle = hourOffsets.find((h) => h.title === trimmed);
			if (byTitle !== undefined) {
				return byTitle.value;
			}
			const plusMatch = trimmed.match(/^\+(\d+)\s+hours?$/i);
			if (plusMatch) {
				const n = parseInt(plusMatch[1], 10);
				if (!Number.isNaN(n)) {
					return Math.min(48, Math.max(0, n));
				}
			}
			if (/^current\s+hour$/i.test(trimmed)) {
				return 0;
			}
		}
		return 0;
	};

	// Current values - make reactive to nodeData changes (aliases match SceneToFlow / WeatherConditionUI)
	$: weatherCond = normalizeWeatherCondKey(nodeData);
	$: rawOperatorKey = normalizeWeatherOperatorKey(nodeData);
	$: value = nodeData.value ?? '';
	$: executionType = nodeData.executionType ?? nodeData.execution_type ?? 'once';
	$: hourOffset = normalizeForecastHourOffset(nodeData);

	// Check if weather station is configured
	$: hasWeatherStation = typeof $user_settings?.weather_station?.l !== 'undefined';
	$: locationId = $user_settings?.weather_station?.l || '';

	// Determine which operators to show
	$: availableOperators =
		weatherCond === 'clouds' || weatherCond === 'wind_direction'
			? equalOnlyOperator
			: numericOperators;
	$: operator = availableOperators.some((o) => o.value === rawOperatorKey)
		? rawOperatorKey
		: availableOperators[0].value;

	// SDS Select compares `getOptionValue(value)` to options and shows `title` only for object values.
	$: weatherParameterOption =
		weatherConditions.find((c) => c.value === weatherCond) ?? weatherConditions[0];
	$: operatorSelectOption =
		availableOperators.find((o) => o.value === operator) ?? availableOperators[0];

	// Update node when values change
	function updateNode() {
		if (!node) return;

		const updatedData = {
			...nodeData,
			sceneData: undefined,
			originalCondition: undefined,
			weatherCond,
			operator,
			value,
			executionType,
			hourOffset,
			locid: locationId,
			label: generateLabel(),
		};

		flowEditorState.updateNode(nodeId, { data: updatedData });
	}

	// Generate label based on conditions
	function generateLabel(): string {
		const condLabel = weatherConditions.find((c) => c.value === weatherCond)?.title || weatherCond;
		const opLabel = operator === 'equal' ? '=' : operator === 'greater_than' ? '>' : '<';
		const valueLabel = getValueLabel();
		const timeLabel = hourOffset === 0 ? 'now' : `+${hourOffset}h`;

		return `${condLabel} ${opLabel} ${valueLabel} (${timeLabel})`;
	}

	function getValueLabel(): string {
		if (weatherCond === 'clouds') {
			return cloudOptions.find((c) => c.value === value)?.title || value;
		} else if (weatherCond === 'wind_direction') {
			return windDirectionOptions.find((w) => w.value === value)?.title || value;
		} else if (weatherCond === 'wind_force') {
			return windForceOptions.find((w) => w.value === value)?.title || value;
		}
		return value.toString();
	}

	// Reset value when condition type changes
	function handleConditionChange(newCond: string) {
		weatherCond = newCond;
		value = ''; // Reset value when switching condition type
		if (newCond === 'clouds' || newCond === 'wind_direction') {
			operator = 'equal';
		}
		updateNode();
	}

	function selectValue(detail: any): any {
		if (typeof detail === 'string' || typeof detail === 'number') return detail;
		if (detail && typeof detail === 'object' && 'value' in detail) return detail.value;
		return detail;
	}

	function handleValueInput(e: any) {
		value = e?.target?.value ?? '';
		updateNode();
	}
	const handleForecastHourChange = (e: Event) => {
		const el = e.currentTarget as HTMLSelectElement;
		const parsed = parseInt(el.value, 10);
		const next = hourOffsets.find((h) => h.value === parsed)?.value ?? 0;
		hourOffset = next;
		updateNode();
	};
</script>

<div class="weather-inspector">
	<!-- Shelly Stack: direction column = horizontal flow; row = vertical stack -->
	<Stack direction="row" gap={16}>
		<div class="weather-section-heading">
			<Text variant="h3">Weather Condition</Text>
		</div>

		{#if !hasWeatherStation}
			<div class="warning-box">
				<i class="fas fa-exclamation-triangle"></i>
				<Text variant="body2">No weather station configured. Please configure in Settings.</Text>
			</div>
		{/if}

		<Collapse title="Condition Settings" expanded={true} id="condition-settings">
			<div class="weather-fields">
				<Stack direction="row" gap={12}>
					<Select
						id="weather-parameter"
						icon=""
						aria-label="Weather Parameter"
						label="Weather Parameter"
						value={weatherParameterOption}
						options={weatherConditions}
						on:change={(e) => handleConditionChange(selectValue(e.detail))}
					/>

					{#if weatherCond !== 'clouds'}
						<Select
							id="weather-operator"
							icon=""
							aria-label="Operator"
							label="Operator"
							value={operatorSelectOption}
							options={availableOperators}
							on:change={(e) => {
								operator = selectValue(e.detail);
								updateNode();
							}}
						/>
					{/if}

					{#if weatherCond === 'clouds'}
						<Select
							id="cloud-coverage"
							icon=""
							aria-label="Cloud Coverage"
							label="Cloud Coverage"
							{value}
							options={cloudOptions}
							on:change={(e) => {
								value = selectValue(e.detail);
								updateNode();
							}}
						/>
					{:else if weatherCond === 'wind_direction'}
						<Select
							id="wind-direction"
							icon=""
							aria-label="Wind Direction"
							label="Direction"
							{value}
							options={windDirectionOptions}
							on:change={(e) => {
								value = selectValue(e.detail);
								updateNode();
							}}
						/>
					{:else if weatherCond === 'wind_force'}
						<Select
							id="wind-force"
							icon=""
							aria-label="Wind Force"
							label="Wind Force"
							{value}
							options={windForceOptions}
							on:change={(e) => {
								value = selectValue(e.detail);
								updateNode();
							}}
						/>
					{:else if weatherCond === 'temperature'}
						<Input
							id="temperature-value"
							aria-label="Temperature Value"
							type="number"
							label="Temperature (°C)"
							{value}
							on:input={handleValueInput}
							placeholder="e.g., 20"
						/>
					{:else if weatherCond === 'humidity'}
						<Input
							id="humidity-value"
							aria-label="Humidity Value"
							type="number"
							label="Humidity (%)"
							{value}
							on:input={handleValueInput}
							placeholder="e.g., 60"
							min="0"
							max="100"
						/>
					{:else if weatherCond === 'wind_speed'}
						<Input
							id="wind-speed-value"
							aria-label="Wind Speed Value"
							type="number"
							label="Wind Speed (km/h)"
							{value}
							on:input={handleValueInput}
							placeholder="e.g., 15"
							min="0"
						/>
					{:else if weatherCond === 'chance_precipitation'}
						<Input
							id="precipitation-value"
							aria-label="Chance of Precipitation Value"
							type="number"
							label="Chance of Precipitation (%)"
							{value}
							on:input={handleValueInput}
							placeholder="e.g., 30"
							min="0"
							max="100"
						/>
					{/if}

					<div class="forecast-hour-field">
						<label class="forecast-hour-label" for="forecast-time">Forecast Time</label>
						<select
							id="forecast-time"
							class="forecast-hour-select"
							aria-label="Forecast Time"
							value={String(hourOffset)}
							on:change={handleForecastHourChange}
						>
							{#each hourOffsets as h}
								<option value={String(h.value)}>{h.title}</option>
							{/each}
						</select>
					</div>

					<Select
						id="execution-type"
						icon=""
						aria-label="Execution Type"
						label="Execution Type"
						value={executionType}
						options={executionTypes}
						on:change={(e) => {
							executionType = selectValue(e.detail);
							updateNode();
						}}
					/>
				</Stack>
			</div>
		</Collapse>

		<div class="info-box">
			<i class="fas fa-info-circle"></i>
			<div class="info-box-text">
				<Text variant="body2">
					Weather conditions use data from OpenWeatherMap. Premium feature.
				</Text>
			</div>
		</div>
	</Stack>
</div>

<style>
	.weather-inspector {
		display: flex;
		flex-direction: column;
		gap: 16px;
		width: 100%;
		min-width: 0;
		box-sizing: border-box;
	}

	.weather-section-heading {
		width: 100%;
		min-width: 0;
	}

	.weather-section-heading :global(h1),
	.weather-section-heading :global(h2),
	.weather-section-heading :global(h3),
	.weather-section-heading :global(h4) {
		word-break: normal;
		overflow-wrap: break-word;
		hyphens: manual;
	}

	.weather-fields {
		width: 100%;
		min-width: 0;
	}

	.weather-fields :global(.stack) {
		width: 100%;
		min-width: 0;
	}

	.forecast-hour-field {
		display: flex;
		flex-direction: column;
		gap: 6px;
		min-width: 0;
		flex: 1;
	}

	.forecast-hour-label {
		color: #9ca3af;
		font-size: 12px;
	}

	.forecast-hour-select {
		height: 32px;
		border-radius: var(--radius-small, 4px);
		border: 1px solid var(--color-3, rgba(255, 255, 255, 0.2));
		background: var(--color-base, rgba(255, 255, 255, 0.06));
		color: var(--color-white, #fff);
		padding: 0 8px;
		font-size: 13px;
		cursor: pointer;
		box-sizing: border-box;
	}

	.warning-box,
	.info-box {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		padding: 12px;
		border-radius: 6px;
		background: rgba(245, 158, 11, 0.1);
		border: 1px solid rgba(245, 158, 11, 0.3);
	}

	.info-box {
		background: rgba(59, 130, 246, 0.1);
		border-color: rgba(59, 130, 246, 0.3);
	}

	.info-box-text {
		flex: 1;
		min-width: 0;
		word-break: normal;
		overflow-wrap: break-word;
	}

	.warning-box i,
	.info-box i {
		color: #f59e0b;
		margin-top: 2px;
		flex-shrink: 0;
	}

	.info-box i {
		color: #3b82f6;
	}
</style>
